// Intersection Observer for scroll animations
const observerOptions = {
    threshold: 0.1,
    rootMargin: '0px 0px -50px 0px'
};

const observer = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
        if (entry.isIntersecting) {
            entry.target.classList.add('animate');
        }
    });
}, observerOptions);

document.addEventListener('DOMContentLoaded', () => {
    const animatedElements = document.querySelectorAll('.scroll-animate');
    animatedElements.forEach(el => observer.observe(el));
    initSkuSearchEnhancements();
});

document.addEventListener('visibilitychange', () => {
    if (document.hidden) {
        document.body.style.animationPlayState = 'paused';
    } else {
        document.body.style.animationPlayState = 'running';
    }
});

function setEqualRatingHeight() {
  const ratingElements = document.querySelectorAll('.rating-with-text');

  if (!ratingElements.length) return;

  ratingElements.forEach((el) => {
    el.style.height = 'auto';
  });

  let maxHeight = 0;

  ratingElements.forEach((el) => {
    if (el.offsetHeight > maxHeight) {
      maxHeight = el.offsetHeight;
    }
  });

  ratingElements.forEach((el) => {
    el.style.height = `${maxHeight}px`;
  });
}

window.addEventListener('load', setEqualRatingHeight);
window.addEventListener('resize', setEqualRatingHeight);

/**
 * SKU search with fallbacks when Shopify predictive search does not index SKUs.
 */
const SKU_SEARCH_SELECTOR = 'form[data-sku-search]';
const HEADER_SEARCH_INPUT_SELECTOR = '[data-header-search-input]';
const SKU_SUGGEST_LIMIT = 10;
const SKU_INDEX_CACHE_KEY = 'beastcom_sku_index_v2';
const SKU_INDEX_MAX_PAGES = 40;
const SKU_AUGMENT_DEBOUNCE_MS = 750;

let skuSearchObserver = null;
let suppressSearchDrawerOpenUntil = 0;

let skuIndexPromise = null;
let skuAugmentTimer = null;
let skuAugmentAbortController = null;

function getShopifyRoot() {
  return window.Shopify?.routes?.root || '/';
}

function normalizeSku(value) {
  return value.trim().toLowerCase();
}

function escapeHtml(value) {
  return String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function getProductHandle(product) {
  if (product?.handle) {
    return product.handle;
  }

  const match = product?.url?.match(/\/products\/([^/?#]+)/);
  return match ? match[1] : null;
}

function getProductImageUrl(product) {
  const src = product?.featured_image || product?.images?.[0]?.src || product?.images?.[0];
  if (!src) {
    return '';
  }

  return src.startsWith('//') ? `https:${src}` : src;
}

function formatVariantPrice(variant) {
  const price = variant?.price;

  if (price == null || price === '') {
    return '';
  }

  const locale = document.documentElement.lang || 'de-DE';
  const currency = window.Shopify?.currency?.active || 'EUR';

  if (typeof price === 'string') {
    const amount = parseFloat(price);
    if (Number.isNaN(amount)) {
      return '';
    }

    return new Intl.NumberFormat(locale, { style: 'currency', currency }).format(amount);
  }

  return new Intl.NumberFormat(locale, { style: 'currency', currency }).format(price / 100);
}

function buildProductMatch(productData, variant, sku) {
  const root = getShopifyRoot();
  return {
    product: productData,
    variant,
    url: `${root}products/${productData.handle}?variant=${variant.id}`
  };
}

function findVariantBySku(productData, sku) {
  if (!productData?.variants?.length) {
    return null;
  }

  const normalizedSku = normalizeSku(sku);
  return productData.variants.find((variant) => variant.sku && normalizeSku(variant.sku) === normalizedSku) || null;
}

async function fetchProductByHandle(handle, signal) {
  const root = getShopifyRoot();
  const response = await fetch(`${root}products/${handle}.js`, { signal });

  if (!response.ok) {
    return null;
  }

  return response.json();
}

async function findViaSuggest(sku, signal) {
  const root = getShopifyRoot();
  const suggestUrl = `${root}search/suggest.json?q=${encodeURIComponent(sku)}&resources[type]=product&resources[limit]=${SKU_SUGGEST_LIMIT}`;
  const suggestResponse = await fetch(suggestUrl, { signal });

  if (!suggestResponse.ok) {
    return null;
  }

  const suggestData = await suggestResponse.json();
  const products = suggestData.resources?.results?.products || [];

  for (const product of products) {
    const handle = getProductHandle(product);
    if (!handle) {
      continue;
    }

    const productData = await fetchProductByHandle(handle, signal);
    const variant = findVariantBySku(productData, sku);

    if (variant) {
      return buildProductMatch(productData, variant, sku);
    }
  }

  return null;
}

async function findViaSearchPage(sku, signal) {
  const root = getShopifyRoot();
  const response = await fetch(`${root}search?q=${encodeURIComponent(sku)}&type=product`, { signal });

  if (!response.ok) {
    return null;
  }

  const html = await response.text();
  const doc = new DOMParser().parseFromString(html, 'text/html');
  const handles = new Set();

  doc.querySelectorAll('a[href*="/products/"]').forEach((link) => {
    const match = link.getAttribute('href')?.match(/\/products\/([^/?#]+)/);
    if (match) {
      handles.add(match[1]);
    }
  });

  for (const handle of handles) {
    const productData = await fetchProductByHandle(handle, signal);
    const variant = findVariantBySku(productData, sku);

    if (variant) {
      return buildProductMatch(productData, variant, sku);
    }
  }

  return null;
}

async function buildSkuIndex(signal) {
  const root = getShopifyRoot();
  const index = {};
  let page = 1;

  while (page <= SKU_INDEX_MAX_PAGES) {
    const response = await fetch(`${root}products.json?limit=250&page=${page}`, { signal });

    if (!response.ok) {
      break;
    }

    const data = await response.json();

    for (const product of data.products || []) {
      for (const variant of product.variants || []) {
        if (!variant.sku) {
          continue;
        }

        index[normalizeSku(variant.sku)] = {
          handle: product.handle,
          title: product.title,
          variantId: variant.id,
          sku: variant.sku,
          price: variant.price,
          featured_image: product.images?.[0]?.src || product.image?.src || ''
        };
      }
    }

    if (!data.products || data.products.length < 250) {
      break;
    }

    page += 1;
  }

  window.__beastcomSkuIndex = index;

  try {
    sessionStorage.setItem(SKU_INDEX_CACHE_KEY, JSON.stringify(index));
  } catch (error) {
    console.warn('Could not cache SKU index', error);
  }

  skuIndexPromise = null;
  return index;
}

async function getSkuIndex(signal) {
  if (window.__beastcomSkuIndex) {
    return window.__beastcomSkuIndex;
  }

  try {
    const cached = sessionStorage.getItem(SKU_INDEX_CACHE_KEY);
    if (cached) {
      window.__beastcomSkuIndex = JSON.parse(cached);
      return window.__beastcomSkuIndex;
    }
  } catch (error) {
    console.warn('Could not read SKU index cache', error);
  }

  if (!skuIndexPromise) {
    skuIndexPromise = buildSkuIndex(signal);
  }

  return skuIndexPromise;
}

function matchFromSkuIndexEntry(entry, sku) {
  if (!entry) {
    return null;
  }

  return {
    product: {
      title: entry.title,
      handle: entry.handle,
      featured_image: entry.featured_image
    },
    variant: {
      id: entry.variantId,
      sku: entry.sku,
      price: entry.price
    },
    url: `${getShopifyRoot()}products/${entry.handle}?variant=${entry.variantId}`
  };
}

async function findViaSkuIndex(sku, signal) {
  const index = await getSkuIndex(signal);
  return matchFromSkuIndexEntry(index[normalizeSku(sku)], sku);
}

async function findViaProductsJsonPage(sku, signal, page = 1) {
  const root = getShopifyRoot();
  const response = await fetch(`${root}products.json?limit=250&page=${page}`, { signal });

  if (!response.ok) {
    return null;
  }

  const data = await response.json();

  for (const product of data.products || []) {
    const variant = findVariantBySku(product, sku);

    if (variant) {
      return buildProductMatch(
        {
          title: product.title,
          handle: product.handle,
          featured_image: product.images?.[0]?.src || ''
        },
        variant,
        sku
      );
    }
  }

  return null;
}

async function findProductMatchBySku(query, signal) {
  const sku = query.trim();
  if (!sku) {
    return null;
  }

  if (window.__beastcomSkuIndex?.[normalizeSku(sku)]) {
    const cachedMatch = matchFromSkuIndexEntry(window.__beastcomSkuIndex[normalizeSku(sku)], sku);
    if (cachedMatch) {
      return cachedMatch;
    }
  }

  const strategies = [findViaProductsJsonPage, findViaSkuIndex, findViaSuggest, findViaSearchPage];

  for (const strategy of strategies) {
    try {
      const match = await strategy(sku, signal);
      if (match) {
        return match;
      }
    } catch (error) {
      if (error.name === 'AbortError') {
        throw error;
      }

      console.warn('SKU search strategy failed', error);
    }
  }

  return null;
}

async function findProductUrlBySku(query, signal) {
  const match = await findProductMatchBySku(query, signal);
  return match?.url || null;
}

function submitSearchForm(form) {
  HTMLFormElement.prototype.submit.call(form);
}

function getSearchDrawer() {
  return document.getElementById('search-drawer');
}

function getSearchDrawerInput() {
  const drawer = getSearchDrawer();
  return drawer?.querySelector('form[data-sku-search] input[name="q"]') || null;
}

function getPredictiveResultsSlot() {
  return document.querySelector('search-drawer predictive-search [slot="results"]');
}

function renderSkuSearchResult(match, query) {
  const label = getSearchDrawer()?.dataset?.skuMatchLabel || 'SKU';
  const imageUrl = getProductImageUrl(match.product);
  const price = formatVariantPrice(match.variant);
  const imageHtml = imageUrl
    ? `<img src="${escapeHtml(imageUrl)}" width="96" height="96" loading="lazy" alt="">`
    : '';

  return `
    <div class="predictive-search__sku-fallback v-stack gap-4 sm:gap-6">
      <p class="text-subdued text-sm">${escapeHtml(label)}: <span class="bold">${escapeHtml(query)}</span></p>
      <a href="${escapeHtml(match.url)}" data-instant class="predictive-search-result group">
        ${imageHtml}
        <div class="v-stack gap-1 sm:gap-1.5">
          <div class="v-stack justify-items-start gap-0.5">
            <span class="bold"><span class="reversed-link">${escapeHtml(match.product.title)}</span></span>
            <span class="text-subdued text-xs">SKU: ${escapeHtml(match.variant.sku)}</span>
            ${price ? `<span class="text-sm">${escapeHtml(price)}</span>` : ''}
          </div>
        </div>
      </a>
    </div>
  `;
}

function preloadSkuIndex() {
  getSkuIndex(new AbortController().signal).catch(() => {});
}

function bindDrawerSearchInput() {
  const input = getSearchDrawerInput();

  if (!input || input.dataset.skuAugmentBound === 'true') {
    return;
  }

  input.dataset.skuAugmentBound = 'true';
  input.addEventListener('input', scheduleSkuAugment);
}

async function augmentPredictiveSearchForSku() {
  const input = getSearchDrawerInput();
  const query = input?.value?.trim();

  if (!query || query.length < 2) {
    return;
  }

  const resultsSlot = getPredictiveResultsSlot();
  const emptyState = resultsSlot?.querySelector('.empty-state');

  if (!resultsSlot || !emptyState) {
    return;
  }

  skuAugmentAbortController?.abort();
  skuAugmentAbortController = new AbortController();
  const { signal } = skuAugmentAbortController;

  const originalHtml = emptyState.innerHTML;
  emptyState.innerHTML = '<p class="h6 text-subdued">SKU wird gesucht…</p>';

  try {
    const match = await findProductMatchBySku(query, signal);

    if (signal.aborted) {
      return;
    }

    const activeEmptyState = getPredictiveResultsSlot()?.querySelector('.empty-state');

    if (!match) {
      if (activeEmptyState) {
        activeEmptyState.innerHTML = originalHtml;
      }
      return;
    }

    if (activeEmptyState) {
      activeEmptyState.outerHTML = renderSkuSearchResult(match, query);
    }
  } catch (error) {
    if (error.name !== 'AbortError') {
      const activeEmptyState = getPredictiveResultsSlot()?.querySelector('.empty-state');
      if (activeEmptyState) {
        activeEmptyState.innerHTML = originalHtml;
      }
    }
  }
}

function scheduleSkuAugment() {
  clearTimeout(skuAugmentTimer);
  skuAugmentTimer = setTimeout(() => {
    augmentPredictiveSearchForSku();
  }, SKU_AUGMENT_DEBOUNCE_MS);
}

function initSkuSearchEnhancements() {
  const drawer = getSearchDrawer();
  const predictiveSearch = drawer?.querySelector('predictive-search');

  if (!drawer || !predictiveSearch) {
    return;
  }

  bindDrawerSearchInput();
  preloadSkuIndex();

  if (!skuSearchObserver) {
    skuSearchObserver = new MutationObserver(() => {
      scheduleSkuAugment();
    });

    skuSearchObserver.observe(predictiveSearch, { childList: true, subtree: true });
  }

  drawer.addEventListener('dialog:after-show', () => {
    bindDrawerSearchInput();
    preloadSkuIndex();
    scheduleSkuAugment();
  });

  drawer.addEventListener('dialog:after-hide', () => {
    suppressSearchDrawerOpen(700);
  });
}

function shouldSuppressSearchDrawerOpen() {
  return Date.now() < suppressSearchDrawerOpenUntil;
}

function suppressSearchDrawerOpen(durationMs = 500) {
  suppressSearchDrawerOpenUntil = Date.now() + durationMs;
}

function openSearchDrawer(query = '') {
  if (shouldSuppressSearchDrawerOpen()) {
    return false;
  }

  const drawer = getSearchDrawer();
  const drawerInput = getSearchDrawerInput();

  if (!drawer || drawer.open) {
    return false;
  }

  if (drawerInput && query) {
    drawerInput.value = query;
    drawerInput.dispatchEvent(new Event('input', { bubbles: true }));
  }

  drawer.show();
  requestAnimationFrame(() => drawerInput?.focus());
  return true;
}

async function runSkuSearchOrRedirect(query) {
  const productUrl = await findProductUrlBySku(query);

  if (productUrl) {
    window.location.assign(productUrl);
    return;
  }

  const root = getShopifyRoot();
  window.location.assign(`${root}search?q=${encodeURIComponent(query)}&type=product`);
}

function syncHeaderSearchToDrawer() {
  const headerInput = document.querySelector(HEADER_SEARCH_INPUT_SELECTOR);
  const drawerInput = getSearchDrawerInput();

  if (!headerInput || !drawerInput || !headerInput.value) {
    return;
  }

  drawerInput.value = headerInput.value;
  drawerInput.dispatchEvent(new Event('input', { bubbles: true }));
}

document.addEventListener('click', (event) => {
  const headerInput = event.target.closest(HEADER_SEARCH_INPUT_SELECTOR);

  if (headerInput) {
    if (!shouldSuppressSearchDrawerOpen()) {
      openSearchDrawer(headerInput.value);
    }
    return;
  }

  const drawerTrigger = event.target.closest('[aria-controls="search-drawer"]');

  if (!drawerTrigger) {
    return;
  }

  requestAnimationFrame(() => {
    const drawer = getSearchDrawer();

    if (drawer?.open) {
      syncHeaderSearchToDrawer();
    }
  });
});

document.addEventListener('keydown', async (event) => {
  if (event.key !== 'Enter' || !event.target.matches(HEADER_SEARCH_INPUT_SELECTOR)) {
    return;
  }

  const query = event.target.value.trim();
  if (!query) {
    return;
  }

  event.preventDefault();
  await runSkuSearchOrRedirect(query);
});

document.addEventListener('submit', async (event) => {
  const form = event.target.closest(SKU_SEARCH_SELECTOR);
  if (!form) {
    return;
  }

  const input = form.querySelector('input[name="q"]');
  const query = input?.value?.trim();

  if (!query) {
    event.preventDefault();
    return;
  }

  event.preventDefault();

  const productUrl = await findProductUrlBySku(query);
  if (productUrl) {
    window.location.assign(productUrl);
    return;
  }

  submitSearchForm(form);
});

if (document.readyState !== 'loading') {
  initSkuSearchEnhancements();
}

/**
 * Collection delivery dates ("Lieferung ab") — must run after facet pagination
 * replaces the section (inline scripts in product cards do not re-execute).
 */
function updateDeliveryDates(root = document) {
  const elements = root.querySelectorAll('.deliver-start-date');

  if (!elements.length) {
    return;
  }

  const deliveryDate = new Date();
  deliveryDate.setDate(deliveryDate.getDate() + 1);

  const formattedDate = [
    String(deliveryDate.getDate()).padStart(2, '0'),
    String(deliveryDate.getMonth() + 1).padStart(2, '0'),
    deliveryDate.getFullYear()
  ].join('.');

  elements.forEach((element) => {
    element.textContent = formattedDate;
  });
}

window.updateDeliveryDate = updateDeliveryDates;

function afterCollectionSectionUpdate() {
  const section = document.querySelector('.shopify-section--main-collection');
  // Update both the section root and the whole document:
  // when Shopify replaces DOM nodes, sometimes the "root" reference can miss newly inserted elements.
  updateDeliveryDates(section || document);
  updateDeliveryDates(document);

  if (typeof window.initLoadMore === 'function') {
    window.initLoadMore();
  }
}

function watchCollectionSectionLoading(section) {
  if (!section) {
    afterCollectionSectionUpdate();
    return;
  }

  if (!section.classList.contains('is-loading')) {
    afterCollectionSectionUpdate();
    return;
  }

  const observer = new MutationObserver(() => {
    if (!section.classList.contains('is-loading')) {
      observer.disconnect();
      afterCollectionSectionUpdate();
    }
  });

  observer.observe(section, { attributes: true, attributeFilter: ['class'] });

  setTimeout(() => {
    observer.disconnect();
    afterCollectionSectionUpdate();
  }, 5000);
}

document.addEventListener('DOMContentLoaded', () => {
  updateDeliveryDates();
});

document.addEventListener('shopify:section:load', (event) => {
  updateDeliveryDates(event.target);
});

document.addEventListener('facet:update', () => {
  watchCollectionSectionLoading(document.querySelector('.shopify-section--main-collection'));
  // Extra safety: ensure dates are set after AJAX DOM replacement finishes.
  setTimeout(() => updateDeliveryDates(), 1500);
});

/* ── Cart drawer upsell slider custom element ────────────────────────────── */
class UpsellSlider extends HTMLElement {
  connectedCallback() {
    this._index = 0;
    this._slides = Array.from(this.querySelectorAll('.upsell-slide'));
    this._prev = this.querySelector('.upsell-prev');
    this._next = this.querySelector('.upsell-next');
    this._counter = this.querySelector('.upsell-counter');

    if (this._slides.length === 0) {
      this.style.display = 'none';
      return;
    }

    if (this._slides.length === 1) {
      const nav = this.querySelector('.cart-drawer__upsell-nav');
      if (nav) nav.style.display = 'none';
    }

    this._prev?.addEventListener('click', () => { this._index--; this._render(); });
    this._next?.addEventListener('click', () => { this._index++; this._render(); });

    this._render();
  }

  _render() {
    this._slides.forEach((slide, i) => slide.classList.toggle('is-active', i === this._index));
    if (this._counter) this._counter.textContent = `${this._index + 1} / ${this._slides.length}`;
    if (this._prev) this._prev.disabled = this._index === 0;
    if (this._next) this._next.disabled = this._index === this._slides.length - 1;
  }
}

if (!customElements.get('upsell-slider')) {
  customElements.define('upsell-slider', UpsellSlider);
}
